import { initialOps, type OpsState } from "./operations";
import { transition } from "xstate";
import { capaMachine, explainCapaBlock, type CapaEvent } from "./capaMachine";
import type { CapaRecord } from "./capa";
import { initialWiki, telemetry, roster } from "../data/catalog";

export type Sample = {
  id: string;
  customer: string;
  order: string;
  product: string;
  instrument: string;
  time: string;
  t90: number;
  status: "Review" | "Held" | "Retested" | "Released";
  retest?: number;
};
export type AuditEvent = {
  id: string;
  at: string;
  actor: string;
  action: string;
  record: string;
  detail: string;
};
export type DemoState = {
  version: 1;
  ops: OpsState;
  lab: string;
  held: boolean;
  investigated: boolean;
  corrected: boolean;
  verified: boolean;
  delivered: boolean;
  watchAdvanced: boolean;
  samples: Sample[];
  capa: CapaRecord;
  audit: AuditEvent[];
  packs: string[];
  wiki: typeof initialWiki;
  work: {
    id: string;
    title: string;
    assignee: string;
    slot: string;
    done: boolean;
  }[];
  exports: {
    id: string;
    at: string;
    format: string;
    sampleIds: string[];
    payload: string;
  }[];
  connectorTests: Record<string, string>;
  modelRuns: Record<string, string>;
  ratings: [number, number, number];
};
export const manager = { name: "Maya Chen", role: "Lab Manager" as const };
export const analyst = { name: "Luis Ortega", role: "Technician" as const };
export function seed(lab = "Houston · Central Laboratory"): DemoState {
  const prefix = lab.includes("Houston") ? "HOU" : "NWK";
  return {
    version: 1,
    ops: initialOps(),
    lab,
    held: false,
    investigated: false,
    corrected: false,
    verified: false,
    delivered: false,
    watchAdvanced: false,
    samples: Array.from({ length: 12 }, (_, i) => ({
      id: `${prefix}-261013-${String(i + 81).padStart(3, "0")}`,
      customer:
        i < 6
          ? "Northstar Fuels"
          : i < 9
            ? "Meridian Trading"
            : "Harbor Energy",
      order: i < 6 ? "NS-1042" : i < 9 ? "MT-8821" : "HE-2209",
      product: i < 9 ? "ULSD · terminal lot" : "Jet A-1 · tank 08",
      instrument: i < 6 ? "DIST-04" : "DIST-02",
      time: ["07:35", "08:10", "08:45", "09:20", "09:55", "10:30"][i % 6],
      t90: Number((i < 6 ? 344.2 + i * 0.36 : 337.1 + i * 0.14).toFixed(1)),
      status: "Review",
    })),
    capa: {
      id: "CAPA-026",
      title: "Recurring heating response drift · DIST-04",
      errorCode: "33",
      instrument: "DIST-04",
      trigger: {
        kind: "repeat",
        note: "Three early-warning windows; repeat of EVT-019.",
      },
      owner: "Maya Chen",
      dueAt: "2026-11-12T17:00:00Z",
      risk: "high",
      status: "open",
      candidateCauses: [
        {
          id: "C-01",
          description: "Poor flask-to-heater contact",
          likelihood: 74,
        },
        { id: "C-02", description: "Temperature probe offset", likelihood: 18 },
        {
          id: "C-03",
          description: "Sample preparation variation",
          likelihood: 8,
        },
      ],
      plan: { actions: [], criteria: { windowDays: 30, maxRecurrences: 0 } },
      history: [],
      createdAt: "2026-10-13T16:12:00Z",
      updatedAt: "2026-10-13T16:12:00Z",
    },
    audit: [
      {
        id: "EVT-001",
        at: "2026-10-13T12:00:00Z",
        actor: "Aisha Patel",
        action: "QC accepted",
        record: "QC-611",
        detail: "CRM-241: T90 338.2 °C; z-score 0.4. DIST-04 in control.",
      },
      {
        id: "EVT-002",
        at: "2026-10-13T12:32:00Z",
        actor: "Drift sentinel · simulated",
        action: "Onset detected",
        record: "TRACE-884",
        detail:
          "Retrospective onset: heater response changed after flask setup. Alert threshold not yet reached.",
      },
      {
        id: "EVT-003",
        at: "2026-10-13T16:12:00Z",
        actor: "Quality agent · simulated",
        action: "Early warning raised",
        record: "ALERT-024",
        detail:
          "Score 0.82 > 0.70. Six samples potentially affected. Next routine QC at 13:00 CDT.",
      },
    ],
    packs: [],
    wiki: structuredClone(initialWiki),
    work: [],
    exports: [],
    connectorTests: {},
    modelRuns: {},
    ratings: [8, 5, 7],
  };
}
export function log(
  s: DemoState,
  action: string,
  record: string,
  detail: string,
  actor = manager.name,
) {
  s.audit.push({
    id: `EVT-${String(s.audit.length + 1).padStart(3, "0")}`,
    at: s.watchAdvanced
      ? "2026-11-13T15:00:00Z"
      : new Date(Date.parse(s.ops.clock) + s.audit.length * 1000).toISOString(),
    actor,
    action,
    record,
    detail,
  });
}
export function capaEvent(s: DemoState, event: CapaEvent) {
  const reason = explainCapaBlock(s.capa, event);
  if (reason) throw new Error(reason);
  const before = capaMachine.resolveState({
    value: s.capa.status,
    context: { capa: s.capa },
  });
  const [after] = transition(capaMachine, before, event);
  s.capa = {
    ...after.context.capa,
    status: after.value as CapaRecord["status"],
    updatedAt: "at" in event ? event.at : s.ops.clock,
    history: [
      ...s.capa.history,
      {
        at: "at" in event ? event.at : s.ops.clock,
        by: event.actor.name,
        role: event.actor.role,
        event: event.type,
        from: s.capa.status,
        to: after.value as CapaRecord["status"],
      },
    ],
  };
  log(
    s,
    event.type.replaceAll("_", " "),
    s.capa.id,
    `${before.value} → ${after.value}`,
    event.actor.name,
  );
}
export type Action =
  | "hold"
  | "investigate"
  | "conclude"
  | "sign"
  | "plan"
  | "approve"
  | "correct"
  | "verify"
  | "release"
  | "watch"
  | "close"
  | "recurrence";
export function act(current: DemoState, action: Action): DemoState {
  const s = structuredClone(current);
  if(s.capa.status==='effectiveness_watch' && action==='recurrence' && s.capa.effectiveness?.result==='passed') throw new Error('This effectiveness window has already passed.');
  const actionTimes: Record<Action, string> = {
    hold: "16:18",
    investigate: "16:50",
    conclude: "16:55",
    sign: "17:00",
    plan: "17:05",
    approve: "17:10",
    correct: "18:10",
    verify: "20:10",
    release: "20:20",
    watch: "20:20",
    close: "20:20",
    recurrence: "20:30",
  };
  const candidateClock = "2026-10-13T" + actionTimes[action] + ":00Z";
  if (candidateClock > s.ops.clock) s.ops.clock = candidateClock;
  const at = new Date(
    Date.parse(s.ops.clock) + s.audit.length * 1000,
  ).toISOString();
  switch (action) {
    case "hold":
      if (s.held) return s;
      if (s.delivered)
        throw new Error(
          "Delivered batch cannot be contained again in this scenario.",
        );
      s.held = true;
      s.samples
        .filter((x) => x.instrument === "DIST-04")
        .forEach((x) => (x.status = "Held"));
      log(
        s,
        "Containment approved",
        "ALERT-024",
        "DIST-04 out of service. Six sample results held; customer delivery blocked.",
      );
      break;
    case "investigate":
      if (!s.held)
        throw new Error("Contain the affected samples before investigation.");
      capaEvent(s, {
        type: "START_INVESTIGATION",
        investigationRecordId: s.capa.investigationRecordId
          ? "WO-301-R" + (s.capa.pastInvestigationIds?.length ?? 0)
          : "WO-301",
        actor: manager,
      });
      s.investigated = true;
      s.work.push({
        id: s.capa.investigationRecordId!,
        title: "Inspect heating assembly and test hypotheses",
        assignee: analyst.name,
        slot: "11:30",
        done: true,
      });
      log(
        s,
        "Investigation evidence recorded",
        "WO-301",
        "Reference probe offset +0.1 °C; flask seating gap 1.8 mm; sample preparation log unchanged.",
        analyst.name,
      );
      break;
    case "conclude":
      capaEvent(s, {
        type: "CONCLUDE_ROOT_CAUSE",
        rootCause: {
          causeId: "C-01",
          statement:
            "Flask seating gap reduced heat transfer after setup; setup checklist omitted a seating check.",
          method: "5why",
          whys: [
            "T90 trended high",
            "Heat transfer was inconsistent",
            "Flask was not seated flush",
            "Setup check omitted seating",
            "SOP revision did not require a recorded inspection",
          ],
        },
        investigationStatus: s.investigated ? "closed" : undefined,
        at,
        actor: analyst,
      });
      break;
    case "sign":
      capaEvent(s, {
        type: "SIGN_ROOT_CAUSE",
        decision: "approved",
        at,
        actor: manager,
      });
      break;
    case "plan":
      capaEvent(s, {
        type: "SUBMIT_PLAN",
        plan: {
          actions: [
            {
              id: "A1",
              kind: "corrective",
              description:
                "Reseat flask; verify with reference thermometer and CRM-241",
              assignee: analyst.name,
              sopId: "SOP-D86-014",
              workRecordId: "WO-302",
            },
            {
              id: "A2",
              kind: "preventive",
              description:
                "Add seating check to setup procedure and brief D86 analysts",
              assignee: "Aisha Patel",
              sopId: "SOP-D86-014",
              workRecordId: "WO-303",
            },
          ],
          criteria: { windowDays: 30, maxRecurrences: 0 },
        },
        at,
        actor: analyst,
      });
      break;
    case "approve":
      capaEvent(s, {
        type: "REVIEW_PLAN",
        decision: "approved",
        at,
        actor: manager,
      });
      s.work.push(
        {
          id: "WO-302",
          title: "Reseat flask + reference check",
          assignee: analyst.name,
          slot: "12:15",
          done: false,
        },
        {
          id: "WO-303",
          title: "Revise setup checklist + analyst briefing",
          assignee: "Aisha Patel",
          slot: "12:45",
          done: false,
        },
      );
      break;
    case "correct":
      if (s.capa.status !== "in_progress")
        throw new Error("Approve the corrective action plan first.");
      s.corrected = true;
      s.work.forEach((w) => (w.done = true));
      log(
        s,
        "Corrective work evidenced",
        "WO-302",
        "Seating gap removed; reference check +0.1 °C; CRM-241 T90 338.3 °C (z = 0.6).",
        analyst.name,
      );
      log(
        s,
        "Preventive work evidenced",
        "WO-303",
        "Setup seating check added; Aisha Patel and Luis Ortega briefed. SOP-D86-014 revision 8.",
        "Aisha Patel",
      );
      s.wiki = s.wiki.map((w) =>
        w.id === "CTX-03"
          ? {
              ...w,
              rev: 8,
              text: w.text + " Record flask seating gap before each batch.",
            }
          : w,
      );
      capaEvent(s, {
        type: "ACTIONS_COMPLETE",
        allActionsClosed: s.work.every((w) => w.done),
        at,
        actor: analyst,
      });
      break;
    case "verify":
      if (!s.corrected) throw new Error("Complete corrective work first.");
      if (s.verified) return s;
      s.verified = true;
      s.samples
        .filter((x) => x.instrument === "DIST-04")
        .forEach((x, i) => {
          x.retest = Number((338.2 + i * 0.19).toFixed(1));
          x.status = "Retested";
        });
      log(
        s,
        "Retest and return-to-service approved",
        "QC-612",
        "Six retained aliquots retested on qualified DIST-02. QC passed. Original results retained and superseded. DIST-04 reference and QC review accepted.",
      );
      break;
    case "release":
      if (
        !s.verified ||
        !s.samples
          .filter((x) => x.instrument === "DIST-04")
          .every((x) => x.status === "Retested")
      )
        throw new Error(
          "Passing QC and all six retests are required before release.",
        );
      s.samples.forEach((x) => (x.status = "Released"));
      s.held = false;
      s.delivered = true;
      log(
        s,
        "Customer release approved",
        "COA-1042",
        "Northstar NS-1042 released using retest results; Meridian and Harbor routine reviews accepted. CAPA remains under effectiveness monitoring.",
      );
      break;
    case "watch":
      if (!s.verified)
        throw new Error(
          "Verify recovery before running the effectiveness scenario.",
        );
      s.watchAdvanced = true;
      capaEvent(s, {
        type: "WATCH_PASSED",
        at: "2026-11-13T15:00:00Z",
        actor: {
          name: "Effectiveness monitor · simulated",
          role: "Viewer",
          system: true,
        },
      });
      break;
    case "close":
      capaEvent(s, {
        type: "CLOSE",
        at: "2026-11-13T15:00:00Z",
        openLinkedWork: s.work.filter((w) => !w.done).map((w) => w.id),
        actor: manager,
      });
      break;
    case "recurrence":
      capaEvent(s, {
        type: "RECURRENCE",
        eventId: "REPEAT-001",
        actor: {
          name: "Recurrence monitor · simulated",
          role: "Viewer",
          system: true,
        },
      });
      s.corrected = false;
      s.verified = false;
      s.held = true;
      s.delivered = false;
      s.samples
        .filter((x) => x.instrument === "DIST-04")
        .forEach((x) => (x.status = "Held"));
      log(
        s,
        "Recurrence containment",
        "REPEAT-001",
        "Affected results re-held; prior export snapshots retained; customer release revoked in this simulation.",
      );
      break;
  }
  return s;
}
export function evidence(s: DemoState) {
  return [
    {
      id: "TRACE-884",
      clause: "6.4 · Equipment",
      title: "Instrument behavior and calibration",
      complete: true,
      detail:
        "28 telemetry observations; calibration CAL-184 valid to 2027-03-01; CRM-241 traceability record.",
    },
    {
      id: "QC-611",
      clause: "7.7 · Validity of results",
      title: "QC history and early-warning timeline",
      complete: true,
      detail:
        "07:00 QC accepted; 11:12 warning; 13:00 scheduled QC. 108-minute lead to next scheduled QC, not a proven detection gain.",
    },
    {
      id: "HOLD-024",
      clause: "7.10 · Nonconforming work",
      title: "Affected samples and containment",
      complete: s.audit.some((e) => e.action === "Containment approved"),
      detail: "Six DIST-04 samples linked by run time, instrument and order.",
    },
    {
      id: "WO-301",
      clause: "7.5 · Technical records",
      title: "Investigation and causal evidence",
      complete: s.investigated,
      detail:
        "Seating gap, probe check, sample preparation review; investigator and independent approval.",
    },
    {
      id: "CAPA-026",
      clause: "8.7 · Corrective actions",
      title: "Approved plan and corrective work",
      complete: s.corrected,
      detail:
        "Corrective and preventive work records, review history, effectiveness status.",
    },
    {
      id: "COMP-041",
      clause: "6.2 · Personnel",
      title: "Analyst competency and procedure",
      complete: true,
      detail:
        "Luis Ortega · D86 Level 3 · COMP-041; controlled SOP revision retained in event history.",
    },
    {
      id: "QC-612",
      clause: "7.7 · Validity of results",
      title: "Recovery and retest evidence",
      complete: s.verified,
      detail:
        "CRM result, alternate-instrument retests and return-to-service authorization.",
    },
    {
      id: "COA-1042",
      clause: "7.8 · Reporting",
      title: "Result authorization and customer delivery",
      complete: s.delivered,
      detail:
        "Original/retest lineage and manager approval; export is a separate recorded action.",
    },
  ];
}
export function binder(s: DemoState) {
  return {
    schema: "trucision.evidence.v1",
    demo: true,
    lab: s.lab,
    scenarioDate: "2026-10-13",
    scope: "NS-1042 / DIST-04 incident",
    notice:
      "Synthetic demonstration records. Clause mapping is illustrative and does not establish ISO/IEC 17025 conformity.",
    manifest: evidence(s),
    samples: s.samples,
    telemetry,
    personnel: roster,
    equipment: {
      instrument: "DIST-04",
      serial: "OD-24189",
      calibration: "CAL-184",
      validUntil: "2027-03-01",
      referenceMaterial: "CRM-241",
      traceability:
        "Synthetic CRM certificate CRM-CERT-241; illustrative reference value 338.0 °C",
    },
    qc: [
      {
        id: "QC-611",
        instrument: "DIST-04",
        at: "2026-10-13T12:00:00Z",
        t90: 338.2,
        zScore: 0.4,
        status: "accepted",
      },
      ...(s.verified
        ? [
            {
              id: "QC-612",
              instrument: "DIST-04",
              at: s.audit.find((e) => e.record === "QC-612")?.at,
              t90: 338.3,
              zScore: 0.6,
              status: "accepted",
              alternateInstrument: "DIST-02",
              alternateQc: { t90: 338.1, zScore: 0.2, status: "accepted" },
            },
          ]
        : []),
    ],
    fmea: {
      id: "FMEA-018",
      ratings: s.ratings,
      policy: "S × O × D; low ≤100, medium ≤500, high >500",
    },
    capa: s.capa,
    work: s.work,
    wiki: s.wiki,
    audit: s.audit,
    exports: s.exports,
  };
}
export function exportPayload(s: DemoState, format: string) {
  if (!s.delivered)
    throw new Error("Only manager-released results can be exported.");
  const rows = s.samples.map((x) => ({
    sample_id: x.id,
    customer: x.customer,
    order: x.order,
    original_instrument: x.instrument,
    result_instrument: x.retest !== undefined ? "DIST-02" : x.instrument,
    result: x.retest ?? x.t90,
    unit: "degC",
    status: x.status,
    supersedes_original: x.retest !== undefined,
  }));
  if (format === "JSON")
    return JSON.stringify(
      { demo: true, approvedBy: manager.name, results: rows },
      null,
      2,
    );
  if (format === "XML")
    return (
      '<?xml version="1.0"?><results demo="true">' +
      rows
        .map(
          (r) =>
            "<result>" +
            Object.entries(r)
              .map(([k, v]) => `<${k}>${v}</${k}>`)
              .join("") +
            "</result>",
        )
        .join("") +
      "</results>"
    );
  return [
    Object.keys(rows[0]).join(","),
    ...rows.map((r) => Object.values(r).join(",")),
  ].join("\n");
}
